import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const StudentProfileReport = ({ 
  isOpen, 
  onClose, 
  selectedStudentId,
  campusName,
  className,
  sectionName
}) => {
  const reportRef = useRef();

  // Dummy comprehensive student profile data
  const studentProfile = {
    id: "STU001",
    fullName: "Ahmed Hassan Ali",
    gender: "Male",
    dateOfBirth: "15/03/2008",
    classGradeSection: "Grade 8 / Section A",
    onboardingDate: "01/09/2023",
    parentName: "Hassan Ali",
    parentContact: "+92-300-1234567",
    address: "House 123, Block A, Gulberg, Lahore",
    status: "Active",
    picture: "https://via.placeholder.com/150x150/4F46E5/FFFFFF?text=AH",
    
    // Attendance Data
    attendance: {
      monthly: {
        "September 2023": { present: 22, absent: 3, total: 25, percentage: 88 },
        "October 2023": { present: 24, absent: 1, total: 25, percentage: 96 },
        "November 2023": { present: 23, absent: 2, total: 25, percentage: 92 },
        "December 2023": { present: 20, absent: 5, total: 25, percentage: 80 },
        "January 2024": { present: 24, absent: 1, total: 25, percentage: 96 },
        "February 2024": { present: 22, absent: 3, total: 25, percentage: 88 }
      },
      overall: { present: 135, absent: 15, total: 150, percentage: 90 }
    },

    // Fee Information
    fees: {
      monthlyFee: 15000,
      discount: 2000,
      currentFee: 13000,
      status: "Paid",
      dueDate: "15/03/2024"
    },

    // Academic Years
    academicYears: [
      {
        year: "2023-24",
        terms: [
          {
            term: "Term 1",
            subjects: [
              { name: "Mathematics", grade: "A+", marks: 95, totalMarks: 100 },
              { name: "English", grade: "A", marks: 88, totalMarks: 100 },
              { name: "Science", grade: "A+", marks: 92, totalMarks: 100 },
              { name: "Urdu", grade: "B+", marks: 82, totalMarks: 100 },
              { name: "Social Studies", grade: "A", marks: 85, totalMarks: 100 }
            ],
            gpa: 3.8,
            percentage: 88.4
          },
          {
            term: "Term 2",
            subjects: [
              { name: "Mathematics", grade: "A+", marks: 97, totalMarks: 100 },
              { name: "English", grade: "A+", marks: 91, totalMarks: 100 },
              { name: "Science", grade: "A", marks: 89, totalMarks: 100 },
              { name: "Urdu", grade: "A", marks: 87, totalMarks: 100 },
              { name: "Social Studies", grade: "A+", marks: 93, totalMarks: 100 }
            ],
            gpa: 3.9,
            percentage: 91.4
          }
        ],
        overallGPA: 3.85,
        overallPercentage: 89.9
      },
      {
        year: "2022-23",
        terms: [
          {
            term: "Term 1",
            subjects: [
              { name: "Mathematics", grade: "A", marks: 87, totalMarks: 100 },
              { name: "English", grade: "B+", marks: 83, totalMarks: 100 },
              { name: "Science", grade: "A", marks: 88, totalMarks: 100 },
              { name: "Urdu", grade: "B", marks: 78, totalMarks: 100 },
              { name: "Social Studies", grade: "A-", marks: 85, totalMarks: 100 }
            ],
            gpa: 3.4,
            percentage: 84.2
          },
          {
            term: "Term 2",
            subjects: [
              { name: "Mathematics", grade: "A+", marks: 94, totalMarks: 100 },
              { name: "English", grade: "A", marks: 86, totalMarks: 100 },
              { name: "Science", grade: "A+", marks: 91, totalMarks: 100 },
              { name: "Urdu", grade: "B+", marks: 81, totalMarks: 100 },
              { name: "Social Studies", grade: "A", marks: 88, totalMarks: 100 }
            ],
            gpa: 3.7,
            percentage: 88.0
          }
        ],
        overallGPA: 3.55,
        overallPercentage: 86.1
      }
    ],

    // Incidents
    incidents: [
      {
        date: "15/11/2023",
        type: "Minor",
        description: "Late arrival to class",
        academicYear: "2023-24",
        status: "Resolved"
      },
      {
        date: "03/12/2023",
        type: "Minor",
        description: "Forgot to bring homework",
        academicYear: "2023-24",
        status: "Resolved"
      }
    ],

    // Classroom Observations
    observations: [
      {
        date: "20/10/2023",
        teacher: "Ms. Sarah Khan",
        subject: "Mathematics",
        observation: "Shows excellent problem-solving skills and helps classmates",
        academicYear: "2023-24"
      },
      {
        date: "15/11/2023",
        teacher: "Mr. Ali Hassan",
        subject: "Science",
        observation: "Very curious and asks thoughtful questions during experiments",
        academicYear: "2023-24"
      },
      {
        date: "10/12/2023",
        teacher: "Ms. Fatima Ahmed",
        subject: "English",
        observation: "Good participation in class discussions, needs improvement in writing",
        academicYear: "2023-24"
      }
    ],

    // AI Recommendations
    aiRecommendations: [
      "Focus on improving Urdu language skills through regular reading practice",
      "Continue excellent performance in Mathematics and Science",
      "Work on time management to reduce late arrivals",
      "Participate more in English writing exercises",
      "Maintain current attendance record"
    ]
  };

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "Student_Profile_Report.pdf", 2);
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["Student Profile Report Summary"],
      ["Student ID", studentProfile.id],
      ["Full Name", studentProfile.fullName],
      ["Campus", campusName],
      ["Class/Section", `${className}/${sectionName}`],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      [],
      ["Basic Information"],
      ["Gender", studentProfile.gender],
      ["Date of Birth", studentProfile.dateOfBirth],
      ["Onboarding Date", studentProfile.onboardingDate],
      ["Parent Name", studentProfile.parentName],
      ["Parent Contact", studentProfile.parentContact],
      ["Address", studentProfile.address],
      ["Status", studentProfile.status],
      [],
      ["Fee Information"],
      ["Monthly Fee", studentProfile.fees.monthlyFee],
      ["Discount", studentProfile.fees.discount],
      ["Current Fee", studentProfile.fees.currentFee],
      ["Fee Status", studentProfile.fees.status],
      [],
      ["Overall Attendance"],
      ["Present Days", studentProfile.attendance.overall.present],
      ["Absent Days", studentProfile.attendance.overall.absent],
      ["Total Days", studentProfile.attendance.overall.total],
      ["Attendance Percentage", studentProfile.attendance.overall.percentage + "%"]
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Academic Performance sheet
    const academicData = [
      ["Academic Performance"],
      ["Academic Year", "Term", "Subject", "Grade", "Marks", "Total Marks", "Percentage"]
    ];

    studentProfile.academicYears.forEach(year => {
      year.terms.forEach(term => {
        term.subjects.forEach(subject => {
          academicData.push([
            year.year,
            term.term,
            subject.name,
            subject.grade,
            subject.marks,
            subject.totalMarks,
            ((subject.marks / subject.totalMarks) * 100).toFixed(1) + "%"
          ]);
        });
      });
    });

    const academicSheet = XLSX.utils.aoa_to_sheet(academicData);
    XLSX.utils.book_append_sheet(workbook, academicSheet, "Academic Performance");

    XLSX.writeFile(workbook, "Student_Profile_Report.xlsx");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[95%] max-h-[95vh] overflow-y-auto relative border border-gray-300">
        
        {/* Student Profile Report - One Page Per Student */}
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 border-b pb-4">
            <h1 className="text-2xl font-bold text-[#241763]">{campusName}</h1>
            <h2 className="text-xl font-semibold mt-2">Student Profile Report</h2>
            <p className="text-base text-gray-600">Academic Year 2023-24</p>
          </div>

          {/* Student Photo and Basic Info */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-4xl font-bold text-gray-600">AH</span>
              </div>
              <p className="text-sm text-gray-600">Student Photo</p>
            </div>
            <div className="col-span-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex">
                  <p className="w-24 font-semibold">Student ID:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.id}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Full Name:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.fullName}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Gender:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.gender}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Date of Birth:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.dateOfBirth}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Class/Section:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.classGradeSection}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Onboarding:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.onboardingDate}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Parent Name:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.parentName}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Contact:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.parentContact}</p>
                </div>
                <div className="flex col-span-2">
                  <p className="w-24 font-semibold">Address:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{studentProfile.address}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Status:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      studentProfile.status.toLowerCase() === "active" 
                        ? "bg-green-100 text-green-800" 
                        : "bg-red-100 text-red-800"
                    }`}>
                      {studentProfile.status}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Status */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Attendance Status</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Monthly Attendance (2023-24)</h4>
                <div className="space-y-2">
                  {Object.entries(studentProfile.attendance.monthly).map(([month, data]) => (
                    <div key={month} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">{month}</span>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm">{data.present}/{data.total}</span>
                        <span className={`text-sm font-semibold ${
                          data.percentage >= 90 ? 'text-green-600' : 
                          data.percentage >= 80 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {data.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Overall Attendance</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {studentProfile.attendance.overall.percentage}%
                    </div>
                    <div className="text-sm text-gray-600">
                      {studentProfile.attendance.overall.present} Present / {studentProfile.attendance.overall.total} Total Days
                    </div>
                    <div className="text-sm text-gray-600">
                      {studentProfile.attendance.overall.absent} Absent Days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fee Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Fee Information</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Monthly Fee</div>
                <div className="text-lg font-bold text-green-600">$. {studentProfile.fees.monthlyFee.toLocaleString()}</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Discount</div>
                <div className="text-lg font-bold text-blue-600">$. {studentProfile.fees.discount.toLocaleString()}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Current Fee</div>
                <div className="text-lg font-bold text-purple-600">$. {studentProfile.fees.currentFee.toLocaleString()}</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Status</div>
                <div className="text-lg font-bold text-orange-600">{studentProfile.fees.status}</div>
              </div>
            </div>
          </div>

          {/* Academic Performance */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Academic Performance</h3>
            {studentProfile.academicYears.map((year, yearIndex) => (
              <div key={year.year} className="mb-4">
                <h4 className="font-semibold text-lg mb-3 text-gray-700">{year.year}</h4>
                {year.terms.map((term, termIndex) => (
                  <div key={term.term} className="mb-4 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-semibold">{term.term}</h5>
                      <div className="flex space-x-4">
                        <span className="text-sm">GPA: <strong>{term.gpa}</strong></span>
                        <span className="text-sm">Percentage: <strong>{term.percentage}%</strong></span>
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {term.subjects.map((subject, subjectIndex) => (
                        <div key={subjectIndex} className="bg-white p-3 rounded text-center">
                          <div className="font-medium text-sm">{subject.name}</div>
                          <div className="text-lg font-bold text-blue-600">{subject.grade}</div>
                          <div className="text-xs text-gray-600">{subject.marks}/{subject.totalMarks}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="bg-blue-100 p-3 rounded-lg text-center">
                  <div className="font-semibold">Overall {year.year}: GPA {year.overallGPA} | Percentage {year.overallPercentage}%</div>
                </div>
              </div>
            ))}
          </div>

          {/* Incidents and Observations */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Incidents Reported</h3>
              <div className="space-y-3">
                {studentProfile.incidents.map((incident, index) => (
                  <div key={index} className="bg-red-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm">{incident.date}</div>
                        <div className="text-sm text-gray-600">{incident.description}</div>
                        <div className="text-xs text-gray-500">{incident.academicYear}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        incident.type === 'Minor' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {incident.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Classroom Observations</h3>
              <div className="space-y-3">
                {studentProfile.observations.map((observation, index) => (
                  <div key={index} className="bg-green-50 p-3 rounded-lg">
                    <div className="font-medium text-sm">{observation.teacher} - {observation.subject}</div>
                    <div className="text-sm text-gray-600 mt-1">{observation.observation}</div>
                    <div className="text-xs text-gray-500 mt-1">{observation.date} | {observation.academicYear}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">AI-Based Recommendations for Improvement</h3>
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
              <ul className="space-y-2">
                {studentProfile.aiRecommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <span className="text-sm">{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Signature Lines */}
          <div className="grid grid-cols-3 gap-8 mt-8 pt-4 border-t">
            <div className="text-center">
              <div className="border-t border-gray-400 mt-8 pt-2">
                <div className="font-semibold">Class Teacher</div>
                <div className="text-sm text-gray-600">Signature</div>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-gray-400 mt-8 pt-2">
                <div className="font-semibold">Headmistress</div>
                <div className="text-sm text-gray-600">Signature</div>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-gray-400 mt-8 pt-2">
                <div className="font-semibold">Principal</div>
                <div className="text-sm text-gray-600">Signature</div>
              </div>
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button 
            onClick={downloadPDF} 
            className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors"
          >
            Download as PDF
          </button>
          <button 
            onClick={downloadExcel} 
            className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors"
          >
            Download as Excel
          </button>
          <button 
            onClick={onClose} 
            className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentProfileReport;


